if (!customElements.get('sticky-atc-bar')) {
  customElements.define(
    'sticky-atc-bar',
    class StickyAtcBar extends HTMLElement {
      constructor() {
        super();
        this.button = this.querySelector('.sticky-atc-bar__submit');
        this.buttonText = this.button?.querySelector('span');
        this.variantChangeUnsubscriber = null;
        this.isMobile = () => window.innerWidth <= 798;
        this.mainAtcButton = null;
      }

      connectedCallback() {
        this.isDesktopVisible = false;
        this.header = document.querySelector('.section-header') || document.querySelector('.section-mobile-header');
        this.announcementBar = document.querySelector('.announcement-bar-section');
        this.mainAtcButton = document.getElementById(`ProductSubmitButton-${this.dataset.sectionId}`);

        this.initEventListeners();
        this.initScrollListener();
        this.initHeaderTransitionListener();

        this.setAttribute('aria-hidden', 'true');
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
        window.removeEventListener('scroll', this.onScroll);
        if (this._positionRafId) cancelAnimationFrame(this._positionRafId);
        this.header?.removeEventListener('transitionstart', this.onHeaderTransitionStart);
        this.header?.removeEventListener('transitionend', this.onHeaderTransitionEnd);
        this.announcementBar?.removeEventListener('transitionstart', this.onHeaderTransitionStart);
        this.announcementBar?.removeEventListener('transitionend', this.onHeaderTransitionEnd);
      }

      isAtcButtonScrolledPast() {
        if (!this.mainAtcButton) return false;
        return this.mainAtcButton.getBoundingClientRect().top < 0;
      }

      getStickyBottomOffset() {
        let bottom = 0;

        if (this.header) {
          const headerRect = this.header.getBoundingClientRect();
          if (headerRect.bottom > 0) {
            bottom = Math.max(bottom, headerRect.bottom);
          }
        }

        if (this.announcementBar && !this.announcementBar.classList.contains('hidden')) {
          const barRect = this.announcementBar.getBoundingClientRect();
          if (barRect.bottom > 0) {
            bottom = Math.max(bottom, barRect.bottom);
          }
        }

        return bottom;
      }

      initScrollListener() {
        let ticking = false;
        this.lastScrollY = window.scrollY;

        this.onScroll = () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(() => {
            ticking = false;

            const y = window.scrollY;
            this.lastScrollY = y;

            if (this.isAtcButtonScrolledPast()) {
              if (!this.isDesktopVisible) this.show();
              if (!this.isMobile()) this.updateDesktopPosition();
            } else {
              if (this.isDesktopVisible) this.hide();
            }
          });
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });
      }

      initHeaderTransitionListener() {
        this._positionRafId = null;

        this.onHeaderTransitionStart = () => {
          if (this._positionRafId) return;
          const track = () => {
            if (this.isDesktopVisible) this.updateDesktopPosition();
            this._positionRafId = requestAnimationFrame(track);
          };
          this._positionRafId = requestAnimationFrame(track);
        };

        this.onHeaderTransitionEnd = () => {
          if (this._positionRafId) {
            cancelAnimationFrame(this._positionRafId);
            this._positionRafId = null;
          }
          if (this.isDesktopVisible) this.updateDesktopPosition();
        };

        this.header?.addEventListener('transitionstart', this.onHeaderTransitionStart);
        this.header?.addEventListener('transitionend', this.onHeaderTransitionEnd);
        this.announcementBar?.addEventListener('transitionstart', this.onHeaderTransitionStart);
        this.announcementBar?.addEventListener('transitionend', this.onHeaderTransitionEnd);
      }

      updateDesktopPosition() {
        if (this.isMobile()) {
          this.style.top = '';
          return;
        }
        const offset = this.getStickyBottomOffset();
        this.style.top = `${offset}px`;
      }

      initEventListeners() {
        this.button?.addEventListener('click', this.onAddToCart.bind(this));

        this.variantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.variantChange,
          this.onVariantChange.bind(this)
        );
      }

      onVariantChange({ data: { sectionId, html, variant } }) {
        if (sectionId !== this.dataset.sectionId) return;

        if (!variant) {
          this.button.disabled = true;
          if (this.buttonText) this.buttonText.textContent = window.variantStrings.unavailable;
          return;
        }

        this.button.dataset.variantId = variant.id;

        if (variant.available) {
          this.button.disabled = false;
          if (this.buttonText) this.buttonText.textContent = window.variantStrings.addToCart;
        } else {
          this.button.disabled = true;
          if (this.buttonText) this.buttonText.textContent = window.variantStrings.soldOut;
        }

        this.updatePrice(variant);
        this.updateImage(variant);
      }

      getFormatter() {
        const currency = window.Shopify?.currency?.active || 'USD';
        if (!this._formatter || this._formatterCurrency !== currency) {
          this._formatterCurrency = currency;
          this._formatter = new Intl.NumberFormat(navigator.language, {
            style: 'currency',
            currency,
            currencyDisplay: 'narrowSymbol',
          });
        }
        return this._formatter;
      }

      updatePrice(variant) {
        const priceEl = this.querySelector(`#StickyAtcBar-Price-${this.dataset.sectionId}`);
        if (!priceEl || !variant.price) return;

        const formatMoney = (cents) => this.getFormatter().format(cents / 100);
        const priceStyle = this.dataset.priceStyle || 'erstyle';
        const isOnSale = variant.compare_at_price && variant.compare_at_price > variant.price;

        if (priceStyle === 'erstyle') {
          if (isOnSale) {
            const save = variant.compare_at_price - variant.price;
            priceEl.innerHTML = `
              <div class="price price--on-sale price--er-style">
                <div class="price__er-container">
                  <span class="price__er-compare">
                    <span class="visually-hidden">Regular price</span>
                    <s class="price-item price-item--regular">
                      ${formatMoney(variant.compare_at_price)}
                    </s>
                  </span>
                  <span class="price__er-now">
                    <span class="visually-hidden">Sale price</span>
                    now ${formatMoney(variant.price)}
                  </span>
                  <span class="price__er-save"> Save ${formatMoney(variant.compare_at_price - variant.price)} </span>
                </div>
              </div>`;
          } else {
            priceEl.innerHTML = `
              <div class="price price--er-style">
                <div class="price__er-container">
                  <span class="price__er-full">
                    <span class="visually-hidden">Regular price</span>
                    <span class="price-item price-item--regular">${formatMoney(variant.price)}</span>
                  </span>
                </div>
              </div>`;
          }
        } else {
          if (isOnSale) {
            priceEl.innerHTML = `
              <div class="price price--on-sale">
                <div class="price__container">
                  <span class="price-item price-item--sale">${formatMoney(variant.price)}</span>
                  <s class="price-item price-item--regular">${formatMoney(variant.compare_at_price)}</s>
                </div>
              </div>`;
          } else {
            priceEl.innerHTML = `
              <div class="price">
                <span class="price-item price-item--regular">${formatMoney(variant.price)}</span>
              </div>`;
          }
        }
      }

      updateImage(variant) {
        if (!variant.featured_image) return;
        const img = this.querySelector('.sticky-atc-bar__img');
        if (img) {
          img.src = variant.featured_image.src;
          img.srcset = '';
          img.alt = variant.featured_image.alt || '';
        }
      }

      show() {
        this.isDesktopVisible = true;
        this.classList.add('is-visible');
        this.setAttribute('aria-hidden', 'false');

        if (!this.isMobile()) {
          this.updateDesktopPosition();
          const mainContent = document.getElementById('MainContent');
          if (mainContent) {
            mainContent.style.paddingTop = `${this.offsetHeight}px`;
          }
        }
        if (window.updateStickyOffset) window.updateStickyOffset();
      }

      hide() {
        this.isDesktopVisible = false;
        this.classList.remove('is-visible');
        this.setAttribute('aria-hidden', 'true');

        if (!this.isMobile()) {
          const mainContent = document.getElementById('MainContent');
          if (mainContent) {
            mainContent.style.paddingTop = '';
          }
        }
        if (window.updateStickyOffset) window.updateStickyOffset();
      }

      async onAddToCart(event) {
        event.preventDefault();
        if (this.button.disabled) return;

        const variantId = this.button.dataset.variantId;
        if (!variantId) return;

        this.button.classList.add('loading');
        this.button.setAttribute('aria-disabled', 'true');
        this.button.querySelector('.loading__spinner')?.classList.remove('hidden');

        try {
          const sectionId = this.dataset.sectionId;
          const mainForm = document.getElementById(`product-form-${sectionId}`);
          const preOrderInput = mainForm?.querySelector('input[name="properties[PreOrder]"]');
          const bodyData = preOrderInput
            ? { id: variantId, quantity: 1, properties: { PreOrder: 'PreOrder' } }
            : { id: variantId, quantity: 1 };

          const data = await CartUtils.addToCartAndOpen(bodyData);

          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'sticky-atc-bar',
            productVariantId: variantId,
            cartData: data,
          });
        } catch (error) {
          console.error('Sticky ATC error:', error);
        } finally {
          this.button.classList.remove('loading');
          this.button.removeAttribute('aria-disabled');
          this.button.querySelector('.loading__spinner')?.classList.add('hidden');
        }
      }

    }
  );
}
